import { PaymentMethodAvailable } from '@/@types/User';
import { cn } from '@/lib/utils';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronDown, Globe, Info, Search, X } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { CircleFlag } from 'react-circle-flags';
import { useTranslation } from 'react-i18next';
import {
  getAlpha2,
  getCountryName,
  getRealCountries,
  isGlobalMethod,
} from './country-registry';

interface PaymentMethodSelectorProps {
  availableMethods: PaymentMethodAvailable[];
  selectedMethods: string[];
  onChange: (methods: string[]) => void;
  /** When true, only methods with supports_recurring can be selected */
  recurringMode?: boolean;
  /** 'default' for offers/integrations, 'compact' for filter sheets */
  variant?: 'default' | 'compact';
  /** Show the "Payment Methods" header with counter. Default: true */
  showHeader?: boolean;
  /** Whether the alternative section is expanded by default. Default: true */
  showAlternativeByDefault?: boolean;
  /** Max height for the scrollable groups area. Default: '350px' */
  maxHeight?: string;
  /** Additional CSS classes on the root container */
  className?: string;
  /** Message shown when no payment methods are available */
  emptyMessage?: string;
}

interface GroupedMethods {
  countryCode: string;
  name: string;
  alpha2: string;
  methods: PaymentMethodAvailable[];
}

const MAIN_METHODS = new Set([
  'credit_card',
  'pix',
  'wallet_google_pay',
  'wallet_apple_pay',
  'paypal',
]);

function isMainMethod(type: string): boolean {
  return MAIN_METHODS.has(type);
}

function groupMethodsByCountry(methods: PaymentMethodAvailable[]): GroupedMethods[] {
  const groupsMap = new Map<string, PaymentMethodAvailable[]>();

  for (const method of methods) {
    const countryCodes = method.countries || [];

    if (countryCodes.length === 0 || isGlobalMethod(countryCodes)) {
      const existing = groupsMap.get('GLOBAL') || [];
      existing.push(method);
      groupsMap.set('GLOBAL', existing);
    } else {
      const realCountries = getRealCountries(countryCodes);
      if (realCountries.length === 0) {
        const existing = groupsMap.get('GLOBAL') || [];
        existing.push(method);
        groupsMap.set('GLOBAL', existing);
      } else {
        for (const code of realCountries) {
          const existing = groupsMap.get(code) || [];
          if (!existing.some(m => m.type === method.type)) {
            existing.push(method);
            groupsMap.set(code, existing);
          }
        }
      }
    }
  }

  const groups: GroupedMethods[] = [];

  for (const [code, groupMethods] of Array.from(groupsMap.entries())) {
    if (code === 'GLOBAL') {
      groups.push({ countryCode: 'GLOBAL', name: 'Global', alpha2: '', methods: groupMethods });
    } else {
      groups.push({
        countryCode: code,
        name: getCountryName(code),
        alpha2: getAlpha2(code),
        methods: groupMethods,
      });
    }
  }

  groups.sort((a, b) => {
    if (a.countryCode === 'GLOBAL') return -1;
    if (b.countryCode === 'GLOBAL') return 1;
    return a.name.localeCompare(b.name);
  });

  return groups;
}

export default function PaymentMethodSelector({
  availableMethods,
  selectedMethods,
  onChange,
  recurringMode = false,
  variant = 'default',
  showHeader = true,
  showAlternativeByDefault = true,
  maxHeight = '350px',
  className,
  emptyMessage,
}: PaymentMethodSelectorProps) {
  const { t } = useTranslation(['common']);
  const isCompact = variant === 'compact';
  const [showAlternative, setShowAlternative] = useState(showAlternativeByDefault);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['GLOBAL']));
  const [searchQuery, setSearchQuery] = useState('');

  const isMethodDisabled = useCallback(
    (method: PaymentMethodAvailable) =>
      recurringMode && method.supports_recurring !== true,
    [recurringMode],
  );

  const validAvailableMethods = useMemo(
    () => availableMethods.filter(m => m.type !== 'paze'),
    [availableMethods]
  );

  const selectableMethods = useMemo(
    () => validAvailableMethods.filter(m => !isMethodDisabled(m)),
    [validAvailableMethods, isMethodDisabled],
  );

  const mainMethods = useMemo(
    () => validAvailableMethods.filter(m => isMainMethod(m.type)),
    [validAvailableMethods],
  );

  const alternativeMethods = useMemo(
    () => validAvailableMethods.filter(m => !isMainMethod(m.type)),
    [validAvailableMethods],
  );

  const alternativeGroups = useMemo(
    () => groupMethodsByCountry(alternativeMethods),
    [alternativeMethods],
  );

  const filteredAlternativeGroups = useMemo(() => {
    if (!searchQuery.trim()) return alternativeGroups;
    const query = searchQuery.toLowerCase();
    return alternativeGroups
      .map(group => ({
        ...group,
        methods: group.methods.filter(
          m => m.name.toLowerCase().includes(query) || m.type.toLowerCase().includes(query),
        ),
      }))
      .filter(group => group.methods.length > 0);
  }, [alternativeGroups, searchQuery]);

  const totalSelected = selectedMethods.filter(m =>
    selectableMethods.some(method => method.type === m)
  ).length;
  const totalAvailable = selectableMethods.length;

  const toggleMethod = useCallback(
    (type: string) => {
      const method = validAvailableMethods.find(m => m.type === type);
      if (method && isMethodDisabled(method)) {
        return;
      }

      if (selectedMethods.includes(type)) {
        onChange(selectedMethods.filter(m => m !== type));
      } else {
        onChange([...selectedMethods, type]);
      }
    },
    [selectedMethods, onChange, validAvailableMethods, isMethodDisabled],
  );

  const toggleSelectAll = useCallback(() => {
    if (totalSelected === totalAvailable) onChange([]);
    else onChange(selectableMethods.map(m => m.type));
  }, [totalSelected, totalAvailable, selectableMethods, onChange]);

  const toggleGroup = useCallback((code: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }, []);

  const toggleGroupAll = useCallback(
    (group: GroupedMethods) => {
      const groupTypes = group.methods.map(m => m.type);
      const allSelected = groupTypes.every(type => selectedMethods.includes(type));
      if (allSelected) {
        onChange(selectedMethods.filter(m => !groupTypes.includes(m)));
      } else {
        onChange(Array.from(new Set([...selectedMethods, ...groupTypes])));
      }
    },
    [selectedMethods, onChange],
  );

  const isGroupAllSelected = (group: GroupedMethods) =>
    group.methods.every(m => selectedMethods.includes(m.type));

  const isGroupPartialSelected = (group: GroupedMethods) =>
    group.methods.some(m => selectedMethods.includes(m.type)) && !isGroupAllSelected(group);

  const getGroupSelectedCount = (group: GroupedMethods) =>
    group.methods.filter(m => selectedMethods.includes(m.type)).length;

  const alternativeSelectedCount = alternativeMethods.filter(m =>
    selectedMethods.includes(m.type),
  ).length;

  const isEmpty = validAvailableMethods.length === 0;

  if (isEmpty && emptyMessage) {
    return (
      <div className={cn(
        'space-y-4 md:space-y-5',
        isCompact && 'space-y-3',
        className,
      )}>
        <div className="flex flex-col items-center justify-center py-6 gap-2 text-center px-4">
          <Info size={18} className="text-zinc-500" />
          <span className="text-xs text-zinc-400 leading-relaxed">{emptyMessage}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(
      'space-y-4 md:space-y-5',
      isCompact && 'space-y-3',
      className,
    )}>
      {showHeader && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2 className={cn(
              'font-semibold text-colorText',
              isCompact ? 'text-base' : 'text-sm',
            )}>{t('payment_methods')}</h2>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-800/80 text-zinc-400 font-medium tabular-nums">
              {totalSelected}/{totalAvailable}
            </span>
          </div>
          <button
            type="button"
            onClick={toggleSelectAll}
            className="text-xs font-medium text-primary hover:text-primary/80 transition-colors active:scale-95"
          >
            {totalSelected === totalAvailable ? t('deselect_all') : t('select_all')}
          </button>
        </div>
      )}

      {!showHeader && (
        <div className="flex items-center justify-between">
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-800/80 text-zinc-400 font-medium tabular-nums">
            {totalSelected}/{totalAvailable}
          </span>
          <button
            type="button"
            onClick={toggleSelectAll}
            className="text-xs font-medium text-primary hover:text-primary/80 transition-colors active:scale-95"
          >
            {totalSelected === totalAvailable ? t('deselect_all') : t('select_all')}
          </button>
        </div>
      )}

      <div className="grid gap-1.5 grid-cols-1 sm:grid-cols-2">
        {mainMethods.map(method => {
          const isSelected = selectedMethods.includes(method.type);
          const disabled = isMethodDisabled(method);
          return (
            <button
              key={method.type}
              type="button"
              disabled={disabled}
              title={disabled ? t('recurring_payment_method_unavailable') : undefined}
              onClick={() => toggleMethod(method.type)}
              className={cn(
                'flex items-center gap-2.5 rounded-lg text-left transition-all duration-150 group/item min-h-[44px]',
                isCompact ? 'px-2.5 py-2.5' : 'px-3 py-2.5',
                disabled
                  ? 'cursor-not-allowed opacity-40 bg-zinc-900/20 ring-1 ring-zinc-800/40'
                  : 'cursor-pointer',
                !disabled && (isSelected
                  ? 'bg-primary/8 ring-1 ring-primary/30'
                  : 'bg-zinc-900/40 hover:bg-zinc-800/50 ring-1 ring-zinc-800/60'),
              )}
            >
              <div
                className={cn(
                  'w-4 h-4 rounded flex items-center justify-center shrink-0 transition-all duration-150',
                  isSelected ? 'bg-primary border-primary' : 'border border-zinc-600 group-hover/item:border-zinc-400',
                )}
              >
                {isSelected && <Check size={10} strokeWidth={3} className="text-black" />}
              </div>
              <span
                className={cn(
                  'text-xs leading-tight truncate transition-colors duration-150',
                  isSelected ? 'text-colorText font-medium' : 'text-zinc-400 group-hover/item:text-zinc-300',
                )}
              >
                {method.name || method.type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </span>
            </button>
          );
        })}
      </div>

      {alternativeMethods.length > 0 && (
        <div className="space-y-3 mt-6 pt-6 border-t border-zinc-800/40">
          <button
            type="button"
            onClick={() => setShowAlternative(!showAlternative)}
            className="w-full flex items-center justify-between group"
          >
            <div className="flex items-center gap-2">
              <h3 className={cn('font-semibold text-colorText', isCompact ? 'text-[13px]' : 'text-sm')}>
                {t('alternative_payment_methods')}
              </h3>
              <div className="relative group/info">
                <Info size={14} className="text-zinc-500 hover:text-zinc-300 transition-colors cursor-help" />
                <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-[11px] text-zinc-300 w-56 opacity-0 invisible group-hover/info:opacity-100 group-hover/info:visible transition-all duration-200 z-10 shadow-xl pointer-events-none">
                  {t('alternative_payment_methods_info')}
                </div>
              </div>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-800/80 text-zinc-400 font-medium tabular-nums">
                {alternativeSelectedCount}/{alternativeMethods.length}
              </span>
            </div>
            <motion.div
              animate={{ rotate: showAlternative ? 180 : 0 }}
              transition={{ duration: 0.2 }}
              className="text-zinc-500"
            >
              <ChevronDown size={16} />
            </motion.div>
          </button>

          <AnimatePresence initial={false}>
            {showAlternative && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25, ease: 'easeInOut' }}
                className="overflow-hidden"
              >
                <div className="space-y-4 pt-2">
                  <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                    <input
                      type="text"
                      placeholder={t('search_payment_method')}
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      className="w-full pl-9 pr-8 py-2 text-xs bg-zinc-900/60 border border-zinc-800 rounded-lg text-colorText placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-primary/40 focus:border-primary/40 transition-all"
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={() => setSearchQuery('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
                      >
                        <X size={12} />
                      </button>
                    )}
                  </div>

                  <div className="space-y-2 overflow-y-auto pr-0.5 scrollbar-thin" style={{ maxHeight }}>
                    {filteredAlternativeGroups.map(group => {
                      const isExpanded = expandedGroups.has(group.countryCode);
                      const allSelected = isGroupAllSelected(group);
                      const partialSelected = isGroupPartialSelected(group);
                      const selectedCount = getGroupSelectedCount(group);

                      return (
                        <div
                          key={group.countryCode}
                          className="rounded-lg border border-zinc-800/80 overflow-hidden bg-zinc-950/40"
                        >
                          <button
                            type="button"
                            onClick={() => toggleGroup(group.countryCode)}
                            className="w-full flex items-center justify-between px-3 py-3 md:py-2.5 hover:bg-zinc-900/60 transition-colors"
                          >
                            <div className="flex items-center gap-2">
                              <div className="w-5 h-5 flex items-center justify-center shrink-0">
                                {group.countryCode === 'GLOBAL' ? (
                                  <Globe size={16} className="text-zinc-400" />
                                ) : (
                                  <div className="w-[18px] h-[18px] rounded-full overflow-hidden flex items-center justify-center">
                                    <CircleFlag countryCode={group.alpha2} height={18} width={18} />
                                  </div>
                                )}
                              </div>
                              <span className="text-[13px] font-medium text-colorText">{group.name}</span>
                              <span
                                className={`
                                  text-[10px] px-1.5 py-px rounded-full font-medium tabular-nums
                                  ${allSelected
                                    ? 'bg-primary/15 text-primary'
                                    : partialSelected
                                      ? 'bg-zinc-800 text-zinc-300'
                                      : 'bg-zinc-800/60 text-zinc-500'
                                  }
                                `}
                              >
                                {selectedCount}/{group.methods.length}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div
                                role="checkbox"
                                aria-checked={allSelected}
                                tabIndex={0}
                                onClick={e => {
                                  e.stopPropagation();
                                  toggleGroupAll(group);
                                }}
                                onKeyDown={e => {
                                  if (e.key === ' ' || e.key === 'Enter') {
                                    e.stopPropagation();
                                    toggleGroupAll(group);
                                  }
                                }}
                                className={`
                                  w-[15px] h-[15px] rounded-[4px] border flex items-center justify-center cursor-pointer transition-all duration-150
                                  ${allSelected
                                    ? 'bg-primary border-primary'
                                    : partialSelected
                                      ? 'border-primary/60 bg-primary/15'
                                      : 'border-zinc-600 hover:border-zinc-400'
                                  }
                                `}
                              >
                                {allSelected && <Check size={10} strokeWidth={3} className="text-black" />}
                                {partialSelected && <div className="w-[7px] h-[2px] bg-primary rounded-full" />}
                              </div>
                              <motion.div
                                animate={{ rotate: isExpanded ? 180 : 0 }}
                                transition={{ duration: 0.2 }}
                                className="text-zinc-500"
                              >
                                <ChevronDown size={14} />
                              </motion.div>
                            </div>
                          </button>

                          <AnimatePresence initial={false}>
                            {isExpanded && (
                              <motion.div
                                initial={{ height: 0 }}
                                animate={{ height: 'auto' }}
                                exit={{ height: 0 }}
                                transition={{ duration: 0.2, ease: 'easeInOut' }}
                                className="overflow-hidden"
                              >
                                <div className="px-2.5 pb-2.5 pt-2 grid gap-1.5 grid-cols-1 sm:grid-cols-2">
                                  {group.methods.map(method => {
                                    const isSelected = selectedMethods.includes(method.type);
                                    const disabled = isMethodDisabled(method);
                                    return (
                                      <button
                                        key={method.type}
                                        type="button"
                                        disabled={disabled}
                                        title={disabled ? t('recurring_payment_method_unavailable') : undefined}
                                        onClick={() => toggleMethod(method.type)}
                                        className={`
                                          flex items-center gap-2 px-2.5 py-2.5 md:py-2 rounded-md text-left transition-all duration-150 group/item min-h-[40px]
                                          ${disabled
                                            ? 'cursor-not-allowed opacity-40'
                                            : 'cursor-pointer'}
                                          ${!disabled && (isSelected
                                            ? 'bg-primary/8 ring-1 ring-primary/30'
                                            : 'hover:bg-zinc-800/50')}
                                        `}
                                      >
                                        <div
                                          className={`
                                            w-3.5 h-3.5 rounded-[3px] border flex items-center justify-center shrink-0 transition-all duration-150
                                            ${isSelected ? 'bg-primary border-primary' : 'border-zinc-600 group-hover/item:border-zinc-400'}
                                          `}
                                        >
                                          {isSelected && <Check size={9} strokeWidth={3} className="text-black" />}
                                        </div>
                                        <span
                                          className={`
                                            text-xs leading-tight truncate transition-colors duration-150
                                            ${isSelected ? 'text-colorText' : 'text-zinc-400 group-hover/item:text-zinc-300'}
                                          `}
                                        >
                                          {method.name || method.type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                                        </span>
                                      </button>
                                    );
                                  })}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}

                    {filteredAlternativeGroups.length === 0 && searchQuery && (
                      <div className="flex flex-col items-center justify-center py-6 gap-2">
                        <Search size={18} className="text-zinc-600" />
                        <span className="text-xs text-zinc-500">
                          {t('no_payment_methods_found')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
